// AI assisted development
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  LinearProgress,
  Tooltip,
  Fab,
  SelectChangeEvent,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import api from '@/services/api';
import { ReleaseNoteStatus } from '@/types';

const JIRA_BASE_URL = import.meta.env.VITE_JIRA_BASE_URL || 'https://upshop-company.atlassian.net';

// TMT/Upshop brand colors
const brandTeal = '#006261';

// Status colors matching Blazor app
const statusColors: Record<ReleaseNoteStatus, { bg: string; color: string; label: string }> = {
  [ReleaseNoteStatus.Pending]: { bg: '#FF9800', color: '#FFFFFF', label: 'Pending' },
  [ReleaseNoteStatus.Generated]: { bg: '#4CAF50', color: '#FFFFFF', label: 'Generated' },
  [ReleaseNoteStatus.Edited]: { bg: '#9C27B0', color: '#FFFFFF', label: 'Edited' },
  [ReleaseNoteStatus.Published]: { bg: '#2196F3', color: '#FFFFFF', label: 'Published' },
  [ReleaseNoteStatus.Failed]: { bg: '#F44336', color: '#FFFFFF', label: 'Failed' },
  [ReleaseNoteStatus.Excluded]: { bg: '#9E9E9E', color: '#FFFFFF', label: 'Excluded' },
};

const ISSUE_TYPE_OPTIONS = ['Story', 'Bug', 'Customer Defect', 'Task', 'Epic'];

const issueTypeColors: Record<string, { bg: string; color: string }> = {
  'Story': { bg: '#4CAF50', color: '#FFFFFF' },
  'Bug': { bg: '#F44336', color: '#FFFFFF' },
  'Customer Defect': { bg: '#FF5722', color: '#FFFFFF' },
  'Task': { bg: '#2196F3', color: '#FFFFFF' },
  'Epic': { bg: '#9C27B0', color: '#FFFFFF' },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [productSuiteFilter, setProductSuiteFilter] = useState<string>(() => searchParams.get('productSuite') ?? '');
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams.get('status') ?? '');
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') ?? '');
  const [issueTypesFilter, setIssueTypesFilter] = useState<string[]>(() => {
    const v = searchParams.get('issueTypes');
    return v ? v.split(',').filter(Boolean) : [];
  });
  const [generatedAfter, setGeneratedAfter] = useState<string | null>(() => searchParams.get('generatedAfter') ?? null);
  const [generatedBefore, setGeneratedBefore] = useState<string | null>(() => searchParams.get('generatedBefore') ?? null);

  // Sync filters to URL when they change (build params from state only; avoid merging with searchParams to keep deps stable)
  useEffect(() => {
    const next = new URLSearchParams();
    if (productSuiteFilter) next.set('productSuite', productSuiteFilter);
    if (statusFilter) next.set('status', statusFilter);
    if (searchTerm) next.set('search', searchTerm);
    if (issueTypesFilter.length) next.set('issueTypes', issueTypesFilter.join(','));
    if (generatedAfter) next.set('generatedAfter', generatedAfter);
    if (generatedBefore) next.set('generatedBefore', generatedBefore);
    setSearchParams(next, { replace: true });
  }, [productSuiteFilter, statusFilter, searchTerm, issueTypesFilter, generatedAfter, generatedBefore, setSearchParams]);

  // Fetch items
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['releaseNoteItems', productSuiteFilter, statusFilter, searchTerm, issueTypesFilter, generatedAfter, generatedBefore],
    queryFn: () => api.getReleaseNoteItems({
      productSuite: productSuiteFilter || undefined,
      status: statusFilter ? parseInt(statusFilter) : undefined,
      searchTerm: searchTerm || undefined,
      issueTypes: issueTypesFilter.length ? issueTypesFilter : undefined,
      generatedAfter: generatedAfter || undefined,
      generatedBefore: generatedBefore || undefined,
      excludePublished: !statusFilter,
      excludeExcluded: !statusFilter || statusFilter !== '5',
    }),
  });

  const { data: itemStats } = useQuery({
    queryKey: ['releaseNoteItemStats'],
    queryFn: () => api.getReleaseNoteItemStats(),
  });

  // Fetch product suites for filter
  const { data: productSuites = [] } = useQuery({
    queryKey: ['productSuites'],
    queryFn: () => api.getProductSuites(),
    select: (data) => Array.isArray(data) ? data : [],
  });

  const items = data?.items || [];

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(items.map((item) => item.id)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedItems(newSelected);
  };

  const handleClearFilters = () => {
    setProductSuiteFilter('');
    setStatusFilter('');
    setSearchTerm('');
    setIssueTypesFilter([]);
    setGeneratedAfter(null);
    setGeneratedBefore(null);
  };

  const handleAddToRelease = () => {
    const selectedIds = Array.from(selectedItems);
    navigate('/publish', { state: { selectedIds } });
  };

  const dashboardPath = location.pathname + location.search;
  const handleRowClick = (id: string, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('input[type="checkbox"]') || target.closest('a')) {
      return;
    }
    navigate(`/editor/${id}`, { state: { returnTo: dashboardPath } });
  };

  const handleOpenEditor = (itemId: string) => {
    navigate(`/editor/${itemId}`, { state: { returnTo: dashboardPath } });
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getStatusChip = (status: ReleaseNoteStatus) => {
    const config = statusColors[status] || statusColors[ReleaseNoteStatus.Pending];
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{ backgroundColor: config.bg, color: config.color, fontWeight: 500 }}
      />
    );
  };

  const getIssueTypeChip = (issueType: string) => {
    const config = issueTypeColors[issueType] || { bg: '#E5E7EB', color: '#374151' };
    return (
      <Chip
        label={issueType}
        size="small"
        sx={{ backgroundColor: config.bg, color: config.color, fontWeight: 500 }}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Release Note Items
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear Filters">
            <IconButton onClick={handleClearFilters}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2, backgroundColor: '#E8F5E9', borderLeft: `4px solid ${brandTeal}` }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Tickets edited (status: Edited)
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, color: brandTeal }}>
          {itemStats?.editedTicketCount ?? '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Count of release note items that have been edited after generation (includes items removed from a published release).
        </Typography>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Product Suite</InputLabel>
              <Select
                value={productSuiteFilter}
                label="Product Suite"
                onChange={(e: SelectChangeEvent) => setProductSuiteFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {Array.isArray(productSuites) && productSuites.map((suite) => (
                  <MenuItem key={suite} value={suite}>{suite}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="0">Pending</MenuItem>
                <MenuItem value="1">Generated</MenuItem>
                <MenuItem value="2">Edited</MenuItem>
                <MenuItem value="3">Published</MenuItem>
                <MenuItem value="4">Failed</MenuItem>
                <MenuItem value="5">Excluded</MenuItem>
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={ISSUE_TYPE_OPTIONS}
              value={issueTypesFilter}
              onChange={(_, newValue) => setIssueTypesFilter(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Issue type"
                  placeholder={issueTypesFilter.length === 0 ? 'All types' : ''}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    sx={{ backgroundColor: issueTypeColors[option]?.bg ?? '#E5E7EB', color: issueTypeColors[option]?.color ?? '#374151' }}
                  />
                ))
              }
              sx={{ minWidth: 280, maxWidth: 420 }}
            />
            <DatePicker
              label="Generated from"
              value={generatedAfter ? new Date(generatedAfter) : null}
              onChange={(d) => setGeneratedAfter(d ? format(d, 'yyyy-MM-dd') : null)}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
            <DatePicker
              label="Generated to"
              value={generatedBefore ? new Date(generatedBefore) : null}
              onChange={(d) => setGeneratedBefore(d ? format(d, 'yyyy-MM-dd') : null)}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
          </Box>
        </LocalizationProvider>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {isLoading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedItems.size > 0 && selectedItems.size < items.length}
                  checked={items.length > 0 && selectedItems.size === items.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell>Jira Key</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Product Suite</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Generated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow
                key={item.id}
                hover
                onClick={(e) => handleRowClick(item.id, e)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedItems.has(item.id)}
                    onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <a
                    href={item.jiraUrl || `${JIRA_BASE_URL}/browse/${item.issueKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ 
                      color: brandTeal, 
                      textDecoration: 'none', 
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {item.issueKey}
                    <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                  </a>
                </TableCell>
                <TableCell>{item.issueType && getIssueTypeChip(item.issueType)}</TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.summary}
                </TableCell>
                <TableCell>
                  {item.productSuite && (
                    <Chip
                      label={item.productSuite}
                      size="small"
                      sx={{ backgroundColor: '#E0F2F1', color: brandTeal, fontWeight: 500 }}
                    />
                  )}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  {item.customer ?? '—'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {getStatusChip(item.status)}
                    {item.status === ReleaseNoteStatus.Published && (item.publishedAt || item.publishedVersion) && (
                      <Typography variant="caption" color="text.secondary">
                        {item.publishedAt && formatDate(item.publishedAt)}
                        {item.publishedAt && item.publishedVersion && ' · '}
                        {item.publishedVersion && `v${item.publishedVersion}`}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {item.generatedAt ? formatDate(item.generatedAt) : '-'}
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpenEditor(item.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No items found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
        Total: {data?.totalCount || 0} items
      </Typography>

      {/* Floating Add to Release button */}
      {selectedItems.size > 0 && (
        <Fab
          variant="extended"
          color="primary"
          onClick={handleAddToRelease}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
        >
          <AddIcon sx={{ mr: 1 }} />
          Add to Release ({selectedItems.size})
        </Fab>
      )}
    </Box>
  );
}
